/**
 * Deep links out of this app to the two surfaces where a client is actually EDITABLE.
 *
 * Field editing in ptc-statetax is deliberately non-obvious, and that friction is wanted —
 * this app is a filing workflow, not a client-master editor, and PTC is transitioning off it.
 * So rather than adding edit affordances here, the client name carries two links out: to the
 * PTC Core CRM client page, and to the underlying Dataverse row in Power Apps. Both targets
 * own their data; this app only reads it.
 *
 * Hostnames follow the convention `config/authConfig.ts` established after the 2026-07-17
 * AADSTS900023 outage: a VITE_ var when present, a built-in constant otherwise, and the
 * literal string "undefined" (which Vite inlines for a missing var) treated as absent. None
 * of these values is a secret — they are org-public URLs that already ship in the bundle.
 */

/** ptc-core's CRM Static Web App (`ptc-core-crm`). */
const CORE_WEB_ORIGIN = 'https://green-water-0afd78b10.7.azurestaticapps.net';

/** The PTC Dataverse organisation this app reads from. */
const DATAVERSE_ORIGIN = 'https://org766b1468.crm.dynamics.com';

/**
 * Dataverse logical name of the state-tax client entity. The OData entity SET is
 * `cr8f7_salestaxclientses` and the primary key `cr8f7_salestaxclientsid`, so the logical
 * name — which is what `etn=` wants — is the singular below.
 */
const STATE_TAX_CLIENT_ENTITY = 'cr8f7_salestaxclients';

function resolveOrigin(value: string | undefined, fallback: string): string {
  const raw = value && value !== 'undefined' ? value : fallback;
  return raw.replace(/\/+$/, ''); // tolerate a trailing slash in the env var
}

/**
 * ptc-core's client (company) page for a Dataverse account GUID.
 *
 * ⚠ **The id-space caveat, taken from ptc-core's own warning rather than assumed away.**
 * `GET /companies/{id}` matches on `Company.Id` ONLY — it is not dual-keyed on `DataverseId`.
 * ptc-core documents that the two id spaces "do NOT reliably coincide", and that the same
 * confusion cost ptc-supervision two failed `USE_SQL_CORE` attempts.
 *
 * This link is nonetheless keyed on the account GUID, because the direction matters:
 *
 * - Every ptc-statetax client IS a Dataverse row (a `cr8f7_salestaxclients` expanded to an
 *   `account`), so `accountId` is always a real account GUID — never a SQL-native id.
 * - ptc-core's load assigns `Id = DataverseId` for the rows it migrated FROM Dataverse, so a
 *   migrated account resolves.
 * - An account ptc-core never migrated therefore yields **a "not found" page, not somebody
 *   else's client.** A SQL-native company would need a random GUID to collide with a
 *   Dataverse account GUID for the link to land on the wrong record.
 *
 * A graceful 404 is an acceptable failure for a convenience link; showing staff the wrong
 * client's record would not be. That asymmetry is the whole reason this is keyed this way,
 * and it is why the caveat is recorded here instead of being discovered again later.
 *
 * @param accountId the Dataverse `account` GUID (`StateTaxClientSummary.accountId`)
 * @returns the URL, or null when there is no account id to link to
 */
export function coreCrmClientUrl(accountId: string | null | undefined): string | null {
  if (!accountId?.trim()) return null;
  const origin = resolveOrigin(import.meta.env.VITE_CORE_WEB_URL, CORE_WEB_ORIGIN);
  return `${origin}/companies/${encodeURIComponent(accountId.trim())}`;
}

/**
 * The Power Apps record page for this app's own Dataverse row.
 *
 * Points at the **`cr8f7_salestaxclients` row, not the account**, on purpose: the ptc-core
 * link above already covers the account/company surface, and the state-tax-specific fields
 * (filing frequency, location of information, notes) live only on this row. Sending both
 * links to the same account would leave those fields unreachable from here. The row's
 * `Client` lookup opens the account in one further click.
 *
 * No `appid` is included. A model-driven-app URL can carry one, but it would pin this link
 * to a specific app registration that is not verifiable from this repo; without it Dataverse
 * routes to the user's default app for the entity, which degrades predictably.
 *
 * @param stateTaxClientId `cr8f7_salestaxclientsid`
 */
export function dataversePowerAppsClientUrl(stateTaxClientId: string | null | undefined): string | null {
  if (!stateTaxClientId?.trim()) return null;
  const origin = resolveOrigin(import.meta.env.VITE_DATAVERSE_URL, DATAVERSE_ORIGIN);
  const params = new URLSearchParams({
    pagetype: 'entityrecord',
    etn: STATE_TAX_CLIENT_ENTITY,
    id: stateTaxClientId.trim(),
  });
  return `${origin}/main.aspx?${params.toString()}`;
}
